import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from '../../environments/environment';
import {v4} from 'uuid'
import { forkJoin, lastValueFrom } from 'rxjs';
import { ExceptionAndLoggingService } from './logging/exception-logging-service';
import { User } from '../common/user';
import { Organisation } from '../common/organisation';
import { CURRENT_STATE_PROJECT_KEY, Linqset } from '../common/linqset-model';
import { LinqException } from '../common/exceptions';

export const ORGANISATION_API = (organisationKey:string, projectKey?:string) => {return projectKey?`api/organisations/${organisationKey}/projects/${projectKey}`:`api/organisations/${organisationKey}`}
export const MODEL_API = (organisationKey:string, modelKey:string, projectKey?:string) => {return ORGANISATION_API(organisationKey, (projectKey||CURRENT_STATE_PROJECT_KEY)) + `/models/${modelKey}`}
export const BACKUP_API = (organisationKey:string, modelKey:string, backupKey?:string, projectKey?:string) => {return MODEL_API(organisationKey, modelKey, projectKey) + (backupKey?'/backups/'+backupKey:'/backups')}

@Injectable({
    providedIn: 'root'
})
export class RepositoryService {
    // identifies this browser tab to the server in every request
    clientKey:string = v4()

    constructor(private http: HttpClient, private exceptionLoggingService:ExceptionAndLoggingService) {
    }

    public getData<T>(route: string) {
        return this.http.get<T>(this.createCompleteRoute(route), this.generateHeaders());
    }

    public create = (route: string, body: any, timeout?:number) => {
        return this.http.post(this.createCompleteRoute(route), body, this.generateHeaders(timeout));
    }

    public update = (route: string, body: any) => {
        return this.http.put(this.createCompleteRoute(route), body, this.generateHeaders());
    }

    public delete = (route: string) => {
        return this.http.delete(this.createCompleteRoute(route), this.generateHeaders());
    }

    // uploads report their progress as events; the last is the response. The browser sets the content type for the
    // form data, with its boundary
    public postFile = (route: string, file: any, timeout?:number) => {
        return this.http.post(this.createCompleteRoute(route), file, {
            headers: new HttpHeaders({'x-client-token': this.clientKey}),
            reportProgress: true,
            observe: 'events',
            timeout
        })
    }

    public putFile = (route: string, file: any, timeout?:number) => {
        return this.http.put(this.createCompleteRoute(route), file, {
            headers: new HttpHeaders({'x-client-token': this.clientKey}),
            reportProgress: true,
            observe: 'events',
            timeout
        })
    }

    // runs several requests together, resolving with each one's result under its name. An error is logged and shown,
    // then passed on
    public async callMultiAPI(subs:any, COMPONENT:string, SERVICE:string, user?:User, organisation?:Organisation, linqset?:Linqset, info?:any) {
        // nothing to ask for: forkJoin of none never gives a value, which lastValueFrom would throw on
        if (!subs || Object.keys(subs).length === 0) return {} as any
        try {
            return await lastValueFrom(forkJoin(subs)) as any
        }catch(err) {
            const e = await this.exceptionLoggingService.handleCaughtError(err, COMPONENT, SERVICE, "Exception while calling server", user, organisation, linqset && linqset.model, info)
            this.exceptionLoggingService.showErrorMessage(e)
            throw e
        }
    }

    // runs a request, resolving with its result. An error is logged and passed on; showError false leaves the caller to
    // show the problem (for background loads that shouldn't interrupt)
    // showError can instead say which errors to show, leaving the others to the caller
    public async callSingleAPI(sub:any, COMPONENT:string, SERVICE:string, user?:User, organisation?:Organisation, linqset?:Linqset, info?:any, showError:boolean|((e:LinqException) => boolean) = true) {
        try {
            return await lastValueFrom(sub) as any
        }catch(err) {
            const e = await this.exceptionLoggingService.handleCaughtError(err, COMPONENT, SERVICE, "Exception while calling server", user, organisation, linqset && linqset.model, info)
            if (typeof showError === "function" ? showError(e) : showError) this.exceptionLoggingService.showErrorMessage(e)
            throw e
        }
    }

    public createCompleteRoute = (route: string) => {
        return `${environment.urlAddress}/${route}`;
    }

    private generateHeaders = (timeout?:number) => {
        return {
            headers: new HttpHeaders({'Content-Type': 'application/json', 'x-client-token':this.clientKey}),
            timeout
        }
    }
}